import axios from "axios";
import { useAuthStore } from "./auth";

export const api = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL ?? "") + "/api/v1",
  headers: { "Content-Type": "application/json" },
  timeout: 10_000,
});

// Attach token on every request
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// ─── API calls ──────────────────────────────────────────────────────────────

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user_id: string;
  role: string;
  name: string;
  hospital_id: string | null;
}

export const authApi = {
  login: (phone: string, password: string) =>
    api.post<LoginResponse>("/auth/login", { phone, password }),
};

export interface Patient {
  id: string;
  hn: string;
  chronic_diseases: string[];
  date_of_birth: string;
  gender: string;
  hospital_id: string;
  user_id: string;
  created_at: string;
}

export interface PatientListResponse {
  data: Patient[];
  total: number;
}

export const patientsApi = {
  list: (params?: { skip?: number; limit?: number }) =>
    api.get<PatientListResponse>("/patients/", { params }),
  get: (id: string) => api.get<Patient>(`/patients/${id}`),
};

export interface AlertEvent {
  id: string;
  patient_id: string;
  level: "level1_patient" | "level2_caregiver" | "level3_hospital" | "level4_emergency";
  status: "pending" | "escalated" | "acknowledged" | "resolved" | "missed";
  scheduled_at: string;
  created_at: string;
  notes: string | null;
}

export interface AlertListResponse {
  data: AlertEvent[];
  total: number;
}

export const alertsApi = {
  list: (params?: { skip?: number; limit?: number; status?: string }) =>
    api.get<AlertListResponse>("/alerts/", { params }),
  pending: () => api.get<{ count: number; data: AlertEvent[] }>("/alerts/pending"),
  acknowledge: (id: string) => api.post(`/alerts/${id}/acknowledge`),
};

export interface AiReason {
  code: string;
  label: string;
  evidence: string;
  weight: number;
}

export interface AiRecommendation {
  type: string;
  label: string;
  priority: "low" | "medium" | "high";
  payload: Record<string, unknown>;
}

export interface AiPriorityPatient {
  patient_id: string;
  hn: string | null;
  risk_score: number;
  risk_level: "low" | "medium" | "high" | "critical";
  summary: string;
  top_reasons: AiReason[];
  recommended_actions: AiRecommendation[];
}

export interface AiBriefingResponse {
  success: boolean;
  data: {
    generated_at: string;
    model: string;
    scope: string;
    risk_counts: Record<"critical" | "high" | "medium" | "low", number>;
    priority_patients: AiPriorityPatient[];
    summary: string;
  };
  message: string;
}

export const aiApi = {
  briefing: () => api.get<AiBriefingResponse>("/ai/dashboard/briefing"),
  // deterministic, no LLM — safe to call without AI flag
  alertExplanation: (id: string) => api.get(`/ai/alerts/${id}/explanation`),
};

export interface HospitalStats {
  total_patients: number;
  pending_alerts: number;
}

export const hospitalsApi = {
  stats: (hospitalId: string) =>
    api.get<HospitalStats>(`/hospitals/${hospitalId}/stats`),
};
